import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logoIglesia from '../../assets/images/logo-oficial.png';
import { usePermissions } from '../../hooks/usePermissions';
import { useContactNotifications } from '../../hooks/useContactNotifications';
import { useChatNotificationsStore } from '../../store/chat-notifications.store';
import {
  Home,
  Users,
  User,
  Shield,
  Globe,
  UserCheck,
  Grid3x3,
  Key,
  Settings,
  Calendar,
  Book,
  DollarSign,
  Heart,
  BarChart3,
  Church,
  Wallet,
  ChevronRight,
  ChevronLeft,
  X,
} from 'lucide-react';

interface Permiso {
  id: string;
  nombre: string;
  descripcion: string;
}

interface Modulo {
  id: string;
  nombre: string;
  descripcion: string;
  ruta: string;
  icono: string;
  orden: number;
  tipo: 'navigation' | 'drawer';
  moduloPadreId?: string;
  permisos: Permiso[];
  submodulos?: Modulo[];
}

interface Rol {
  id: string;
  nombre: string;
  descripcion: string;
  modulos: Modulo[];
}

interface Usuario {
  id: string;
  email: string;
  nombreCompleto: string;
  tipoUsuario: string;
  estado: boolean;
  roles: Rol[];
}

interface SidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  isMobileMenuOpen?: boolean;
  onCloseMobileMenu?: () => void;
}

// Mapeo de iconos usando Lucide React
const iconMap: Record<string, React.ComponentType<any>> = {
  dashboard: Home,
  users: Users,
  UsersIcon: Users,
  UserIcon: User,
  IdentificationIcon: User,
  GlobeAltIcon: Globe,
  ShieldCheckIcon: Shield,
  UserGroupIcon: Users,
  Squares2X2Icon: Grid3x3,
  KeyIcon: Key,
  'user-cog': Settings,
  shield: Shield,
  calendar: Calendar,
  book: Book,
  'dollar-sign': DollarSign,
  heart: Heart,
  'chart-bar': BarChart3,
  church: Church,
  settings: Settings,
  Wallet: Wallet,
};

export const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  setIsCollapsed,
  isMobileMenuOpen,
  onCloseMobileMenu,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, user } = usePermissions();
  const { totalNuevos } = useContactNotifications();
  const chatNoLeidos = useChatNotificationsStore((state) => state.totalNoLeidos);

  // Simplificar: usar sessionStorage para persistir el estado entre re-renders
  const [activeDrawer, setActiveDrawerState] = React.useState<string | null>(() => {
    return sessionStorage.getItem('activeDrawer');
  });

  const setActiveDrawer = React.useCallback((drawerId: string | null) => {
    if (drawerId) {
      sessionStorage.setItem('activeDrawer', drawerId);
    } else {
      sessionStorage.removeItem('activeDrawer');
    }
    setActiveDrawerState(drawerId);
  }, []);

  // Obtener todos los módulos ordenados
  const modulosOrdenados = React.useMemo(() => {
    if (!user?.roles) return [];

    const modulosMap = new Map<string, Modulo>();

    // Recopilar todos los módulos de todos los roles
    user.roles.forEach((rol: any) => {
      rol.modulos.forEach((modulo: any) => {
        if (modulosMap.has(modulo.id)) {
          // Si ya existe, combinar permisos únicos
          const existing = modulosMap.get(modulo.id)!;
          const permisosUnicos = new Map();

          [...existing.permisos, ...modulo.permisos].forEach((permiso) => {
            permisosUnicos.set(permiso.id, permiso);
          });

          existing.permisos = Array.from(permisosUnicos.values());
        } else {
          modulosMap.set(modulo.id, { ...modulo });
        }
      });
    });

    // Convertir a array y ordenar
    return Array.from(modulosMap.values()).sort((a, b) => a.orden - b.orden);
  }, [user]);

  // Crear configuración de drawers dinámicamente desde los módulos
  const drawerModules = React.useMemo(() => {
    const drawers: Record<string, any> = {};

    // Identificar módulos drawer (aquellos con ruta null)
    const modulosDrawer = modulosOrdenados.filter((modulo) => modulo.ruta === null);

    modulosDrawer.forEach((drawerModulo) => {
      // Buscar submódulos que corresponden a este drawer
      let submodulos: any[] = [];

      if (drawerModulo.nombre === 'Seguridad') {
        submodulos = modulosOrdenados.filter(
          (modulo) =>
            modulo.ruta && modulo.ruta.startsWith('/seguridad/') && hasPermission(modulo.nombre, 'ver'),
        );
      } else if (drawerModulo.nombre === 'Miembros') {
        submodulos = modulosOrdenados.filter(
          (modulo) =>
            modulo.ruta && modulo.ruta.startsWith('/miembros/') && hasPermission(modulo.nombre, 'ver'),
        );
      } else if (drawerModulo.nombre === 'Configuración') {
        submodulos = modulosOrdenados.filter(
          (modulo) =>
            modulo.ruta && modulo.ruta.startsWith('/config/') && hasPermission(modulo.nombre, 'ver'),
        );
      } else if (drawerModulo.nombre === 'Gestión Web') {
        submodulos = modulosOrdenados.filter(
          (modulo) =>
            modulo.ruta && modulo.ruta.startsWith('/gestion-web/') && hasPermission(modulo.nombre, 'ver'),
        );
      } else if (drawerModulo.nombre === 'Iglesia') {
        submodulos = modulosOrdenados.filter(
          (modulo) =>
            modulo.ruta && modulo.ruta.startsWith('/iglesia/') && hasPermission(modulo.nombre, 'ver'),
        );
      }

      // Solo crear drawer si tiene submódulos
      if (submodulos.length > 0) {
        drawers[drawerModulo.id] = {
          id: drawerModulo.id,
          nombre: drawerModulo.nombre,
          descripcion: drawerModulo.descripcion,
          icono: drawerModulo.icono,
          tabs: submodulos
            .sort((a, b) => a.orden - b.orden)
            .map((sub: Modulo) => ({
              id: sub.id,
              nombre: sub.nombre,
              ruta: sub.ruta,
            })),
        };
      }
    });

    return drawers;
  }, [modulosOrdenados, hasPermission]);

  const handleModuleClick = (modulo: Modulo) => {
    // Para módulos drawer, abrir drawer en lugar de navegar
    if (
      modulo.nombre === 'Miembros' ||
      modulo.nombre === 'Seguridad' ||
      modulo.nombre === 'Iglesia' ||
      modulo.nombre === 'Configuración'
    ) {
      const newActiveDrawer = activeDrawer === modulo.id ? null : modulo.id;
      setActiveDrawer(newActiveDrawer);
      // En móvil, no cerrar al abrir un drawer padre (para que pueda ver submódulos)
      return;
    }

    // Eventos dentro del dashboard
    if (modulo.nombre === 'Eventos') {
      navigate('/dashboard/eventos');
      if (onCloseMobileMenu) onCloseMobileMenu();
      return;
    }

    // Mayordomía dentro del dashboard
    if (modulo.nombre === 'Mayordomía') {
      navigate('/dashboard/mayordomia');
      if (onCloseMobileMenu) onCloseMobileMenu();
      return;
    }

    // Navegación normal para otros módulos
    if (modulo.ruta) {
      navigate(modulo.ruta);
      if (onCloseMobileMenu) onCloseMobileMenu();
    }
  };

  interface DrawerTab {
    id: string;
    nombre: string;
    ruta: string;
  }

  const isActiveRoute = (ruta: string) => {
    if (!ruta) return false;
    // Para la ruta raíz del dashboard, solo debe estar activa si estamos exactamente en /dashboard
    if (ruta === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    // Para otras rutas, verificar tanto la ruta directa como con prefijo /dashboard
    return (
      location.pathname === ruta ||
      location.pathname.startsWith(`${ruta}/`) ||
      location.pathname === `/dashboard${ruta}` ||
      location.pathname.startsWith(`/dashboard${ruta}/`)
    );
  };

  const isModuleActive = (modulo: Modulo) => {
    // Verificar si es un módulo drawer
    const drawerConfig = drawerModules[modulo.id as keyof typeof drawerModules];
    if (drawerConfig) {
      // Para módulos drawer, verificar si alguna de sus rutas está activa
      return drawerConfig.tabs.some(
        (tab: DrawerTab) =>
          location.pathname === `/dashboard${tab.ruta}` ||
          location.pathname.startsWith(`/dashboard${tab.ruta}/`),
      );
    }
    // Eventos: también reconocer la ruta del dashboard
    if (modulo.nombre === 'Eventos') {
      return location.pathname === '/dashboard/eventos' || location.pathname === '/eventos';
    }
    // Para módulos normales, verificar la ruta directa
    return isActiveRoute(modulo.ruta);
  };

  // useRef para drawerModules para evitar que el effect se re-ejecute
  // cuando drawerModules cambia por re-renders (no por cambios de ruta reales)
  const drawerModulesRef = React.useRef(drawerModules);
  drawerModulesRef.current = drawerModules;

  // Auto-abrir drawer cuando la ruta actual coincide con un submódulo.
  // Solo debe reaccionar a cambios de location.pathname, NO a clicks manuales
  // en los drawers padre (que no cambian la ruta).
  React.useEffect(() => {
    if (!location.pathname.startsWith('/dashboard')) return;

    for (const [drawerId, drawer] of Object.entries(drawerModulesRef.current)) {
      const hasActiveTab = drawer.tabs.some(
        (tab: DrawerTab) =>
          location.pathname === `/dashboard${tab.ruta}` ||
          location.pathname.startsWith(`/dashboard${tab.ruta}/`),
      );
      if (hasActiveTab) {
        setActiveDrawer(drawerId);
        return;
      }
    }
  }, [location.pathname, setActiveDrawer]);

  return (
    <>
      {/* Overlay backdrop para móvil */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-30 lg:hidden transition-opacity duration-300"
          onClick={onCloseMobileMenu}
        />
      )}

      <aside
        className={`
          ${isCollapsed ? 'lg:w-14' : 'lg:w-56'}
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
          fixed lg:relative
          left-0 top-0
          h-screen
          w-56
          z-50
          bg-gradient-to-b from-white via-purple-50/30 to-white text-gray-700 flex flex-col transition-all duration-200 ease-in-out border-r border-gray-200 overflow-hidden
        `}
      >
        {/* Botón cerrar en móvil */}
        <button
          onClick={onCloseMobileMenu}
          className="lg:hidden absolute top-2 right-2 p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors z-10"
          aria-label="Cerrar menú"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Logo institucional — clic para colapsar/expandir el sidebar */}
        <button
          type="button"
          onClick={() => {
            setIsCollapsed(!isCollapsed);
            if (onCloseMobileMenu) onCloseMobileMenu();
          }}
          title={isCollapsed ? 'Expandir sidebar' : 'Contraer sidebar'}
          aria-label={isCollapsed ? 'Expandir sidebar' : 'Contraer sidebar'}
          className="flex items-center justify-center h-14 lg:h-16 w-full border-b border-gray-200/60 shrink-0 cursor-pointer"
        >
          <img
            src={logoIglesia}
            alt="ICC Logo"
            className={`object-contain transition-all duration-200 ${
              isCollapsed ? 'w-10 h-10 lg:w-8 lg:h-8' : 'w-10 h-10 lg:w-11 lg:h-11'
            }`}
          />
        </button>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto overflow-x-hidden">
          {modulosOrdenados
            .filter((modulo) => {
              // Para módulos drawer (ruta null), verificar si tiene al menos un submódulo con permiso de ver
              if (modulo.ruta === null) {
                // Buscar submódulos que corresponden a este drawer
                let tieneSubmodulosConPermisos = false;

                if (modulo.nombre === 'Miembros') {
                  tieneSubmodulosConPermisos = modulosOrdenados.some(
                    (submodulo) =>
                      submodulo.ruta &&
                      submodulo.ruta.startsWith('/miembros/') &&
                      hasPermission(submodulo.nombre, 'ver'),
                  );
                } else if (modulo.nombre === 'Seguridad') {
                  tieneSubmodulosConPermisos = modulosOrdenados.some(
                    (submodulo) =>
                      submodulo.ruta &&
                      submodulo.ruta.startsWith('/seguridad/') &&
                      hasPermission(submodulo.nombre, 'ver'),
                  );
                } else if (modulo.nombre === 'Configuración') {
                  tieneSubmodulosConPermisos = modulosOrdenados.some(
                    (submodulo) =>
                      submodulo.ruta &&
                      submodulo.ruta.startsWith('/config/') &&
                      hasPermission(submodulo.nombre, 'ver'),
                  );
                } else if (modulo.nombre === 'Gestión Web') {
                  tieneSubmodulosConPermisos = modulosOrdenados.some(
                    (submodulo) =>
                      submodulo.ruta &&
                      submodulo.ruta.startsWith('/gestion-web/') &&
                      hasPermission(submodulo.nombre, 'ver'),
                  );
                } else if (modulo.nombre === 'Iglesia') {
                  tieneSubmodulosConPermisos = modulosOrdenados.some(
                    (submodulo) =>
                      submodulo.ruta &&
                      submodulo.ruta.startsWith('/iglesia/') &&
                      hasPermission(submodulo.nombre, 'ver'),
                  );
                }

                return tieneSubmodulosConPermisos;
              }

              // Para módulos normales, verificar si tiene permiso de ver
              const tienePermisoVer = hasPermission(modulo.nombre, 'ver');
              if (!tienePermisoVer) return false;

              // Verificar si es un submódulo de algún drawer
              const esSubmodulo =
                modulo.ruta &&
                (modulo.ruta.startsWith('/seguridad/') ||
                  modulo.ruta.startsWith('/miembros/') ||
                  modulo.ruta.startsWith('/config/') ||
                  modulo.ruta.startsWith('/gestion-web/') ||
                  modulo.ruta.startsWith('/iglesia/'));

              return !esSubmodulo; // Mostrar solo si NO es submódulo
            })
            .map((modulo) => {
              const isActive = isModuleActive(modulo);
              const drawerConfig = drawerModules[modulo.id as keyof typeof drawerModules];
              const isDrawerOpen = activeDrawer === modulo.id;
              const IconComponent = iconMap[modulo.icono] || Settings;

              return (
                <div key={modulo.id} className="relative group">
                  <button
                    onClick={() => handleModuleClick(modulo)}
                    className={`flex items-center rounded-md transition-colors duration-150 group/item ${
                      isCollapsed
                        ? 'lg:w-9 lg:h-9 lg:p-0 lg:mx-auto lg:justify-center w-full px-3 py-1.5'
                        : 'w-full px-3 py-1.5'
                    } ${
                      isActive
                        ? 'bg-purple-50 text-purple-900 border-l-2 border-purple-600'
                        : 'text-gray-600 hover:bg-purple-50/60 hover:text-purple-700'
                    }`}
                  >
                    {/* Icono */}
                    <div
                      className={`flex-shrink-0 relative ${
                        isCollapsed ? 'lg:mr-0 mr-2.5' : 'mr-2.5'
                      }`}
                    >
                      <IconComponent
                        className={`h-4 w-4 transition-colors duration-150 ${
                          isActive ? 'text-gray-800' : 'text-gray-500 group-hover/item:text-gray-700'
                        }`}
                        strokeWidth={isActive ? 2 : 1.5}
                      />

                      {/* Badge de mensajes nuevos en módulo Iglesia */}
                      {modulo.nombre === 'Iglesia' && (totalNuevos + chatNoLeidos) > 0 && (
                        <div className="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full px-1">
                          {(totalNuevos + chatNoLeidos) > 99
                            ? '99+'
                            : (totalNuevos + chatNoLeidos)}
                        </div>
                      )}
                    </div>

                    {/* Texto del módulo */}
                    <div
                      className={`flex-1 text-left transition-all duration-200 ease-in-out overflow-hidden whitespace-nowrap ${
                        isCollapsed
                          ? 'lg:opacity-0 lg:max-w-0 opacity-100 max-w-[160px]'
                          : 'opacity-100 max-w-[160px]'
                      }`}
                    >
                      <span
                        className={`text-xs font-medium transition-colors duration-150 ${
                          isActive ? 'text-purple-900' : 'text-gray-600 group-hover/item:text-purple-700'
                        }`}
                      >
                        {modulo.nombre}
                      </span>
                    </div>

                    {/* Indicador de flecha para módulos drawer */}
                    <div
                      className={`flex-shrink-0 transition-all duration-200 ease-in-out overflow-hidden ${
                        isCollapsed
                          ? 'lg:opacity-0 lg:max-w-0 opacity-100 max-w-[18px]'
                          : 'opacity-100 max-w-[18px]'
                      }`}
                    >
                      {drawerConfig ||
                      modulo.nombre === 'Miembros' ||
                      modulo.nombre === 'Seguridad' ||
                      modulo.nombre === 'Iglesia' ||
                      modulo.nombre === 'Configuración' ? (
                        <ChevronRight
                          className={`w-3.5 h-3.5 transition-transform duration-150 ${
                            isDrawerOpen ? 'rotate-90' : 'rotate-0'
                          } ${isActive ? 'text-purple-700' : 'text-gray-400 group-hover:text-purple-500'}`}
                        />
                      ) : null}
                    </div>
                  </button>

                  {/* Mostrar submódulos cuando el drawer está abierto */}
                  <div
                    className={`ml-6 mt-0.5 space-y-0.5 border-l border-purple-200/60 pl-3 transition-all duration-200 ease-in-out overflow-hidden ${
                      isDrawerOpen && !isCollapsed
                        ? 'max-h-96 opacity-100 transform translate-y-0'
                        : 'max-h-0 opacity-0 transform -translate-y-1'
                    }`}
                  >
                    {modulosOrdenados
                      .filter((submodulo) => {
                        // Primero verificar si el usuario tiene permiso de "ver" este submódulo
                        const tienePermisoVer = hasPermission(submodulo.nombre, 'ver');
                        if (!tienePermisoVer) return false;

                        if (modulo.nombre === 'Miembros') {
                          return submodulo.ruta && submodulo.ruta.startsWith('/miembros/');
                        }
                        if (modulo.nombre === 'Seguridad') {
                          return submodulo.ruta && submodulo.ruta.startsWith('/seguridad/');
                        }
                        if (modulo.nombre === 'Iglesia') {
                          return submodulo.ruta && submodulo.ruta.startsWith('/iglesia/');
                        }
                        if (modulo.nombre === 'Configuración') {
                          return submodulo.ruta && submodulo.ruta.startsWith('/config/');
                        }
                        return false;
                      })
                      .sort((a, b) => a.orden - b.orden)
                      .map((submodulo) => {
                        const subActive = isActiveRoute(`/dashboard${submodulo.ruta}`);
                        return (
                          <button
                            key={submodulo.id}
                            onClick={() => {
                              navigate(`/dashboard${submodulo.ruta}` || '#');
                              if (onCloseMobileMenu) onCloseMobileMenu();
                            }}
                            className={`w-full flex items-center px-2 py-1 rounded-md text-xs transition-colors duration-150 ${
                              subActive
                                ? 'bg-purple-50 text-purple-700 font-medium'
                                : 'text-gray-600 hover:bg-purple-50/50 hover:text-purple-700'
                            }`}
                          >
                            <span className="truncate">{submodulo.nombre}</span>
                          </button>
                        );
                      })}
                  </div>

                  {/* Tooltip para modo colapsado — solo desktop */}
                  {isCollapsed && (
                    <div className="hidden lg:block fixed left-14 px-2 py-1.5 bg-gray-800 text-white text-xs rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 whitespace-nowrap z-50 border border-gray-700 ml-1">
                      <div className="font-medium">{modulo.nombre}</div>
                      <div className="absolute top-2 -left-1 w-2 h-2 bg-gray-800 border-l border-b border-gray-700 rotate-45" />
                    </div>
                  )}
                </div>
              );
            })}
        </nav>

        {/* Botón flotante para contraer/expandir sidebar — solo desktop */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hidden lg:flex absolute right-1 bottom-16 w-6 h-6 bg-white border border-gray-200 rounded-full shadow-sm hover:shadow items-center justify-center transition-all duration-150 hover:bg-gray-50 z-10"
          title={isCollapsed ? 'Expandir sidebar' : 'Contraer sidebar'}
        >
          {isCollapsed ? (
            <ChevronRight className="w-3 h-3 text-gray-600" />
          ) : (
            <ChevronLeft className="w-3 h-3 text-gray-600" />
          )}
        </button>
      </aside>
    </>
  );
};
